import { useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';

import { MAX_IMAGE_COUNT, resizeImages } from '@/shared/lib/utils/image';

import { Button } from '../button';
import { Input } from '../input/Input';

export interface ImagePickerMethods {
  getFiles: () => File[];
  reset: () => void;
}

interface ImagePickerProps {
  onChange?: (files: File[]) => void;
  maxCount?: number;
}

interface PreviewItem {
  file: File;
  url: string;
}

const ImagePicker = forwardRef<ImagePickerMethods, ImagePickerProps>(
  ({ onChange, maxCount = MAX_IMAGE_COUNT }, ref) => {
    const [previews, setPreviews] = useState<PreviewItem[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      getFiles: () => previews.map(p => p.file),
      reset: () => {
        previews.forEach(p => URL.revokeObjectURL(p.url));
        setPreviews([]);
        onChange?.([]);
      },
    }));

    const handleFiles = useCallback(
      async (fileList: FileList) => {
        const incoming = Array.from(fileList);
        const remaining = maxCount - previews.length;
        if (remaining <= 0) return;

        const selected = incoming.slice(0, remaining);
        const resized = await resizeImages(selected);

        const newPreviews = resized.map(file => ({
          file,
          url: URL.createObjectURL(file),
        }));

        setPreviews(prev => {
          const next = [...prev, ...newPreviews];
          onChange?.(next.map(p => p.file));
          return next;
        });
      },
      [previews.length, maxCount, onChange],
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
        e.target.value = '';
      }
    };

    const removeImage = (index: number) => {
      setPreviews(prev => {
        URL.revokeObjectURL(prev[index].url);
        const next = prev.filter((_, i) => i !== index);
        onChange?.(next.map(p => p.file));
        return next;
      });
    };

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          {previews.map((preview, index) => (
            <div key={preview.url} className="relative w-[72px] h-[72px] rounded-lg overflow-hidden border border-border">
              <img
                src={preview.url}
                alt={`preview-${index}`}
                className="w-full h-full object-cover"
              />
              <Button
                variant="ghost"
                type="button"
                onClick={() => removeImage(index)}
                className="h-5 w-5 p-0 absolute top-0 right-0 rounded-bl-md bg-black/60 text-xs text-white hover:bg-black/60"
              >
                X
              </Button>
            </div>
          ))}
          {previews.length < maxCount && (
            <Button
              variant="ghost"
              type="button"
              onClick={() => inputRef.current?.click()}
              className="h-[72px] w-[72px] rounded-lg border-2 border-dashed border-muted-foreground/40 text-muted-foreground transition-colors hover:border-muted-foreground hover:bg-transparent"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </Button>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {previews.length}/{maxCount}
        </span>
        <Input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleChange}
          className="hidden"
        />
      </div>
    );
  },
);

ImagePicker.displayName = 'ImagePicker';

export default ImagePicker;
